<template>
  <div>
    <pending-banner />
    <div class="row mb-2">
      <div class="col-12">
        <div
          v-if="
            !dismissedPending && dashboard.pending_duty_shift_allocations > 0
          "
          role="alert"
          class="alert alert-warning alert-dismissible fade show d-flex align-items-center mb-2"
          style="gap: 8px"
        >
          <feather-icon icon="ClockIcon" size="16" class="mr-1" />
          <span>
            Há
            <strong>{{ dashboard.pending_duty_shift_allocations }}</strong>
            solicitação(ões) de plantão aguardando aprovação.
            <a href="/plantoes" class="alert-link ml-1"
              >Clique aqui para verificar</a
            >
          </span>
          <button
            type="button"
            class="close ml-auto"
            aria-label="Close"
            @click="dismissedPending = true"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div
          v-if="
            !dismissedUnfilled &&
            dashboard.unfilled_duty_shifts &&
            dashboard.unfilled_duty_shifts.length > 0
          "
          role="alert"
          class="alert alert-warning alert-dismissible fade show d-flex align-items-center mb-0"
          style="gap: 8px"
        >
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-1" />
          <span v-if="dashboard.unfilled_duty_shifts.length === 1">
            Plantão
            <strong>{{ dashboard.unfilled_duty_shifts[0].title }}</strong> está
            sem colaborador alocado.
            <a href="/plantoes" class="alert-link ml-1"
              >Clique aqui para verificá-lo</a
            >
          </span>
          <span v-else>
            Há
            <strong>{{ dashboard.unfilled_duty_shifts.length }}</strong>
            plantões sem colaboradores alocados nos próximos 3 dias.
            <a href="/plantoes" class="alert-link ml-1"
              >Clique aqui para verificar</a
            >
          </span>
          <button
            type="button"
            class="close ml-auto"
            aria-label="Close"
            @click="dismissedUnfilled = true"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div id="card-pending" class="col-md">
        <b-card
          no-body
          class="dashboard-card"
          role="button"
          tabindex="0"
          @click="goTo('/checkRequests')"
          @keyup.enter="goTo('/checkRequests')"
        >
          <b-card-body class="pb-0">
            <b-avatar class="mb-1" variant="light-warning" size="45">
              <feather-icon size="21" icon="AlertCircleIcon" />
            </b-avatar>
            <div class="truncate mb-2">
              <h2 class="mb-25 font-weight-bolder">
                {{ dashboard.pending_check_requests }}
              </h2>
              <strong>Solicitações de marcação em aberto</strong>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div id="card-days" class="col-md">
        <b-card
          no-body
          class="dashboard-card"
          role="button"
          tabindex="0"
          @click="goTo('/myChecks')"
          @keyup.enter="goTo('/myChecks')"
        >
          <b-card-body class="pb-0">
            <b-avatar class="mb-1" variant="light-primary" size="45">
              <feather-icon size="21" icon="CalendarIcon" />
            </b-avatar>
            <div class="truncate mb-2">
              <h2 class="mb-25 font-weight-bolder">
                {{ dashboard.days_worked_month }}
              </h2>
              <strong>Dias trabalhados (mês)</strong>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div id="card-hours" class="col-md">
        <b-card no-body class="dashboard-card" role="button" tabindex="0">
          <b-card-body class="pb-0">
            <b-avatar class="mb-1" variant="light-success" size="45">
              <feather-icon size="21" icon="ClockIcon" />
            </b-avatar>
            <div class="truncate mb-2">
              <h2 class="mb-25 font-weight-bolder">
                {{ formattedWorkedHours }}
              </h2>
              <strong>Horas trabalhadas (mês)</strong>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div id="card-bank" class="col-md">
        <b-card no-body class="dashboard-card" role="button" tabindex="0">
          <b-card-body class="pb-0">
            <b-avatar class="mb-1" variant="light-info" size="45">
              <feather-icon size="21" icon="PocketIcon" />
            </b-avatar>
            <div class="truncate mb-2">
              <h2 class="mb-25 font-weight-bolder">
                {{ formattedBankHours }}
              </h2>
              <strong>Banco de horas</strong>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/libs/axios";
import { BCard, BCardBody, BAvatar, BCardText, BLink } from "bootstrap-vue";
import PendingBanner from "@/components/PendingBanner.vue";

export default {
  name: "Home",
  components: {
    BCard,
    BCardBody,
    BAvatar,
    BCardText,
    BLink,
    PendingBanner,
  },
  data() {
    return {
      dashboard: {
        pending_check_requests: 0,
        days_worked_month: 0,
        worked_hours_month: 0,
        bank_hours: 0,
        unfilled_duty_shifts: [],
        pending_duty_shift_allocations: 0,
      },
      loading: false,
      errors: null,
      lastUpdated: null,
      dismissedPending: false,
      dismissedUnfilled: false,
    };
  },
  computed: {
    formattedWorkedHours() {
      return `${Number(this.dashboard.worked_hours_month).toFixed(2)}h`;
    },
    formattedBankHours() {
      const v = Number(this.dashboard.bank_hours);
      const sign = v > 0 ? "+" : "";
      return `${sign + v.toFixed(2)}h`;
    },
    lastUpdatedText() {
      return this.lastUpdated
        ? new Date(this.lastUpdated).toLocaleString()
        : "—";
    },
  },
  async mounted() {
    this.pendingBanner();
    this.getDashboard();    
  },

  methods: {
    async getDashboard() {
      this.loading = true;
      this.errors = null;
      try {
        const api = `${this.$store.state.api}dashboard`;
        const res = await axios.get(api);
        if (res && res.data && res.data.success) {
          this.dashboard = Object.assign(this.dashboard, res.data.data || {});
          this.lastUpdated = Date.now();
        } else {
          this.errors =
            res && res.data ? JSON.stringify(res.data) : "Resposta inválida";
        }
      } catch (err) {
        this.errors =
          err.response && err.response.data
            ? JSON.stringify(err.response.data)
            : err.message || err;
      } finally {
        this.loading = false;
      }
    },

    goTo(route) {
      if (!route) return;
      try {
        this.$router.push(route);
      } catch (e) {
        console.error("Navigation error:", e);
      }
    },
  },
};
</script>

<style scoped>
.dashboard-card {
  border-radius: 8px;
  transition: transform 180ms ease, box-shadow 180ms ease;
  cursor: pointer;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  border: 1px solid rgba(16, 24, 40, 0.04);
}
.dashboard-card:hover,
.dashboard-card:focus {
  transform: translateY(-6px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.12);
  outline: none;
}
.dashboard-card:active {
  transform: translateY(-2px);
}
.dashboard-summary {
  border-radius: 10px;
  transition: box-shadow 180ms ease;
}
.dashboard-summary:hover {
  box-shadow: 0 8px 18px rgba(16, 24, 40, 0.08);
}
.mb-25 {
  margin-bottom: 0.25rem;
}
.font-weight-bolder {
  font-weight: 700;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-layout .dashboard-card {
  background: #1e293b;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
  border: 1px solid rgba(16, 24, 40, 0.12);
}
</style>
